import { format, formatDistanceToNowStrict } from 'date-fns';
import { tz } from '@date-fns/tz';

export const DEFAULT_TIME_ZONE = 'UTC';
const UTC_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

export function normalizeEditorTimeZone(timeZone) {
  return typeof timeZone === 'string' && timeZone.trim() ? timeZone.trim() : DEFAULT_TIME_ZONE;
}

export function getUserTimeZone(userInfo) {
  return normalizeEditorTimeZone(userInfo?.timeZone ?? userInfo?.timezone);
}

function normalizeEditorDateInput(input) {
  if (typeof input !== 'string') {
    return input;
  }

  const value = input.trim();

  if (!value) {
    return value;
  }

  // Backend timestamps are returned without an offset and should be treated as UTC.
  if (UTC_DATE_TIME_PATTERN.test(value)) {
    return `${value.replace(' ', 'T')}Z`;
  }

  return value;
}

function parseEditorDate(input) {
  if (input == null || input === '') {
    return null;
  }

  const normalizedInput = normalizeEditorDateInput(input);
  const date = input instanceof Date ? new Date(input.getTime()) : new Date(normalizedInput);

  return Number.isNaN(date.getTime()) ? null : date;
}

function withTimeZoneFallback(timeZone, formatter) {
  const normalizedTimeZone = normalizeEditorTimeZone(timeZone);

  try {
    return formatter(normalizedTimeZone);
  } catch {
    if (normalizedTimeZone === DEFAULT_TIME_ZONE) {
      return '';
    }

    try {
      return formatter(DEFAULT_TIME_ZONE);
    } catch {
      return '';
    }
  }
}

export function formatEditorDate(input, timeZone = DEFAULT_TIME_ZONE) {
  const date = parseEditorDate(input);

  if (!date) {
    return '';
  }

  return withTimeZoneFallback(timeZone, (resolvedTimeZone) =>
    format(date, 'MMM d, yyyy', { in: tz(resolvedTimeZone) }),
  );
}

export function formatEditorDistanceToNow(input, timeZone = DEFAULT_TIME_ZONE) {
  const date = parseEditorDate(input);

  if (!date) {
    return '';
  }

  return withTimeZoneFallback(timeZone, (resolvedTimeZone) =>
    formatDistanceToNowStrict(date, { addSuffix: true, in: tz(resolvedTimeZone) }),
  );
}
